import { Button, Flex, Form, Modal, Skeleton, Tabs } from "antd"
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { MAIN_CONTENT_STICKY_BOTTOM_CLASS } from "../../../components/main-content.ts"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { BranchConfigurationTab } from "../../Branches/pages/tabs/BranchConfigurationTab.tsx"
import {
  selectActiveBranchId,
  selectCompanyIsMultiBranch
} from "../../Branches/redux/branches.slice.ts"
import { CompanyDefaultsSubTabs } from "../components/common/CompanyDefaultsSubTabs.tsx"
import { AutomaticProjectCreationCard } from "../components/settings-configuration/AutomaticProjectCreationCard.tsx"
import { CompanyDefaultsConfigCard } from "../components/settings-configuration/CompanyDefaultsConfigCard.tsx"
import {
  LeadProjectIdCard,
  type LeadProjectIdFormData
} from "../components/settings-configuration/LeadProjectIdCard.tsx"
import { ProjectListSettingsCard } from "../components/settings-configuration/ProjectListSettingsCard.tsx"
import { SmsRetentionCard } from "../components/settings-configuration/SmsRetentionCard.tsx"
import { SmsSendWindowCard } from "../components/settings-configuration/SmsSendWindowCard.tsx"
import { SubcontractorAlertsCard } from "../components/settings-configuration/SubcontractorAlertsCard.tsx"
import {
  fetchIdConfiguration,
  updateIdConfiguration
} from "../redux/settings.thunks.ts"

const DEFAULTS: LeadProjectIdFormData = { mode: "incremental", start_from: 1 }

const TABS = ["ids", "projects", "subcontractors", "sms"] as const
type ConfigurationTab = (typeof TABS)[number]

const asTab = (value: string | null): ConfigurationTab =>
  TABS.includes(value as ConfigurationTab) ? (value as ConfigurationTab) : "ids"

export default function SettingsConfigurationPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isMultiBranch = useAppSelector(selectCompanyIsMultiBranch)
  const activeBranchId = useAppSelector(selectActiveBranchId)
  // A branch workspace manages ITS OWN effective configuration (inherited
  // from Company HQ, overridable per branch) rather than the companywide
  // defaults every branch inherits from — reuses the same effective-
  // configuration screen/endpoint Settings -> Branches -> [branch] already
  // exercises, instead of building a second one.
  const isBranchScope = isMultiBranch && !!activeBranchId
  const config = useAppSelector((state) => state.settings.idConfiguration)
  const canEditCompanySettings = useAppSelector(
    (state) =>
      !!state.auth?.userInfo?.permissions?.codes?.settings?.company
        ?.settings_company_edit
  )
  const userId = useAppSelector((state) => state.auth?.userInfo?.id)
  const fetching = useAppSelector(
    (state) => state.settings.idConfigurationFetching
  )
  const saving = useAppSelector((state) => state.settings.idConfigurationSaving)

  const [params, setParams] = useSearchParams()
  const tab = asTab(params.get("tab"))
  const onTabChange = (next: string) =>
    setParams((current) => {
      const updated = new URLSearchParams(current)
      if (next === "ids") updated.delete("tab")
      else updated.set("tab", next)
      return updated
    })

  const [form] = Form.useForm<LeadProjectIdFormData>()
  const [dirty, setDirty] = useState(false)

  const locked = config?.start_locked === true

  const initialValues = useMemo<LeadProjectIdFormData>(
    () => ({
      mode: config?.mode ?? DEFAULTS.mode,
      start_from: config?.start_from ?? DEFAULTS.start_from
    }),
    [config]
  )

  // Layout effect, not a plain one: dispatching before paint means
  // `idConfigurationFetching` flips true before the browser ever shows a
  // frame, so the form's DEFAULTS never flash before the loading skeleton.
  useLayoutEffect(() => {
    generateDocumentTitle("Settings Configurations")
    // The company-wide ID configuration has no branch scope of its own —
    // skip it while a branch workspace is active, since that branch
    // renders its own effective-configuration screen instead.
    if (!isBranchScope) void dispatch(fetchIdConfiguration())
  }, [dispatch, isBranchScope])

  useEffect(() => {
    // Only resync from the server while the form has no unsaved edit of its
    // own — otherwise a slow initial fetch resolving after the user has
    // already started typing would silently discard what they typed.
    if (!dirty) form.setFieldsValue(initialValues)
  }, [form, initialValues, dirty])

  const persist = async (values: LeadProjectIdFormData) => {
    const payload =
      values.mode === "incremental"
        ? { mode: values.mode, start_from: values.start_from }
        : { mode: values.mode }
    try {
      await dispatch(updateIdConfiguration(payload)).unwrap()
      setDirty(false)
    } catch {
      // errors surface through the shared response handler
    }
  }

  const handleFinish = (values: LeadProjectIdFormData) => {
    const willLock = !locked && values.mode === "incremental"
    if (willLock) {
      Modal.confirm({
        title: t("settingsConfiguration.confirmLock.title"),
        content: t("settingsConfiguration.confirmLock.content"),
        okText: t("settingsConfiguration.confirmLock.okText"),
        onOk: () => persist(values)
      })
      return
    }
    void persist(values)
  }

  const idsTab =
    fetching && !config ? (
      <Flex flex={1}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Flex>
    ) : (
      <Form
        form={form}
        layout="vertical"
        className="flex-1 flex gap-3 flex-col w-full with-default-error"
        initialValues={initialValues}
        disabled={saving}
        onValuesChange={() => setDirty(true)}
        onFinish={handleFinish}
      >
        <div className="space-y-3 flex-1">
          <LeadProjectIdCard form={form} locked={locked} />
        </div>

        <Flex
          justify="end"
          className={`lg:flex-row max-lg:items-center ${MAIN_CONTENT_STICKY_BOTTOM_CLASS} z-[2] bg-background py-2`}
        >
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            disabled={!dirty}
          >
            {t("settingsConfiguration.save")}
          </Button>
        </Flex>
      </Form>
    )

  if (isBranchScope) {
    return (
      <div className="flex-1 flex flex-col gap-3 w-full">
        <CompanyDefaultsSubTabs />
        <BranchConfigurationTab branchId={activeBranchId!} onSaved={() => {}} />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col gap-3 w-full">
      <CompanyDefaultsSubTabs />
      <CompanyDefaultsConfigCard />
      <Tabs
        activeKey={tab}
        onChange={onTabChange}
        items={[
          {
            key: "ids",
            label: t("settingsConfiguration.tabs.ids"),
            children: idsTab
          },
          {
            key: "projects",
            label: t("settingsConfiguration.tabs.projects"),
            children: (
              <Flex vertical gap={12}>
                <ProjectListSettingsCard />
                <AutomaticProjectCreationCard />
              </Flex>
            )
          },
          {
            key: "subcontractors",
            label: t("settingsConfiguration.tabs.subcontractors"),
            children: <SubcontractorAlertsCard />
          },
          {
            key: "sms",
            label: t("settingsConfiguration.tabs.sms"),
            children: (
              <Flex vertical gap={12}>
                <SmsRetentionCard
                  readonly={!canEditCompanySettings}
                  draftOwner={userId ? String(userId) : undefined}
                />
                <SmsSendWindowCard
                  readonly={!canEditCompanySettings}
                  draftOwner={userId ? String(userId) : undefined}
                />
              </Flex>
            )
          }
        ]}
      />
    </div>
  )
}
